import type { MathJsInstance } from 'mathjs';
import { all, create } from 'mathjs';

/**
 * Names blocked by the proxy below from resolving on the shared mathjs instance. `config` would let
 * any caller mutate precision/behavior for every later calculation; `createunit` is a lowercased,
 * inert placeholder mirroring the calculation engine's own function blacklist.
 */
const BLOCKED_FUNCTION_NAMES = new Set(['config', 'createunit']);

let instance: MathJsInstance | undefined;

/**
 * The one mathjs instance for calculations: building one costs far more than a calculation, and
 * every settle pass runs every expression. Don't reconfigure it; a caller needing other config
 * (e.g. `number: 'BigNumber'`) builds its own.
 */
export const getMathjsInstance = (): MathJsInstance => {
    if (!instance) {
        const created = create(all, {});

        instance = new Proxy(created, {
            get: (target, property) =>
                typeof property === 'string' && BLOCKED_FUNCTION_NAMES.has(property)
                    ? undefined
                    : target[property as keyof MathJsInstance],
        });
    }

    return instance;
};
